import { PlusIcon, ViewColumnsIcon } from "@heroicons/react/24/outline";
import { RegionalScope } from "@sp-crm/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SupportEmailLink } from "components/app/support-email-link";
import { UpsellBanner } from "components/app/upsell-banner";
import { ContentFull, ContentHeader, ContentSubheader } from "components/layout";
import { PipelineSummary } from "components/referral-pipelines/types";
import { trpc } from "components/rpc/trpc";
import { DeleteButton, EditButton } from "components/ui/action-button";
import { ErrorMessage } from "components/ui/error-message";
import { InlineBanner } from "components/ui/inline-banner";
import { Input } from "components/ui/input";
import { InternalLink } from "components/ui/internal-link";
import { defaultLinkStyle } from "components/ui/link";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import { Toggle } from "components/ui/toggle";
import { Formik, FormikHelpers } from "formik";
import React, { useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useProductName } from "store/selectors/branding";
import { handleEvent } from "util/user-events";
import { ReferralPipelineStageEditor } from "./referral-pipeline-stage-editor";

interface PipelineNameFormValues {
    name: string;
}

export const ReferralPipelinesSettings: React.FC<unknown> = () => {
    const trpcClient = trpc.useTRPC();
    const queryClient = useQueryClient();
    const pipelinesQuery = useQuery({
        ...trpcClient.pipeline.agencyList.queryOptions(),
    });

    const refetchPipelines = useCallback(async () => {
        await queryClient.invalidateQueries({ queryKey: [["pipeline"]] });
    }, [queryClient]);

    if (pipelinesQuery.isPending) {
        return <Spinner />;
    }

    if (pipelinesQuery.isError) {
        return (
            <ErrorMessage component="ReferralPipelinesSettings">
                <pre>{JSON.stringify(pipelinesQuery.error, null, 2)}</pre>
            </ErrorMessage>
        );
    }

    return (
        <ContentFull>
            <ContentHeader>Referral pipelines</ContentHeader>
            <ContentSubheader>
                Pipelines are boards where referral contacts and referral organizations
                move through stages. Stages appear as columns on the pipeline board.
            </ContentSubheader>
            <div className="max-w-2xl space-y-4">
                {(pipelinesQuery.data ?? []).map(pipeline => (
                    <ReferralPipelineSettings
                        key={pipeline.id}
                        pipeline={pipeline}
                        scope={{ kind: "agencyWide" }}
                        onChanged={refetchPipelines}
                    />
                ))}
                <AddReferralPipelineSection
                    scope={{ kind: "agencyWide" }}
                    label="Add referral pipeline"
                    onCreated={refetchPipelines}
                />
            </div>
        </ContentFull>
    );
};

interface AddReferralPipelineSectionProps {
    scope: RegionalScope;
    label: string;
    onCreated: () => void;
}

export const AddReferralPipelineSection: React.FC<
    AddReferralPipelineSectionProps
> = props => {
    const { scope, label, onCreated } = props;
    const trpcClient = trpc.useTRPC();
    const canCreateQuery = useQuery({ ...trpcClient.pipeline.canCreate.queryOptions() });
    const createMutation = useMutation({
        ...trpcClient.pipeline.create.mutationOptions(),
    });
    const [isAdding, setIsAdding] = React.useState(false);
    const startAdding = useCallback(() => setIsAdding(true), []);
    const stopAdding = useCallback(() => setIsAdding(false), []);

    const atPipelineLimit = canCreateQuery.data === "TooMany";

    const save = useCallback(
        async (
            values: PipelineNameFormValues,
            formik: FormikHelpers<PipelineNameFormValues>,
        ) => {
            const name = values.name.trim();
            if (!name || atPipelineLimit) {
                return;
            }
            await createMutation.mutateAsync({ name, scope });
            handleEvent("referral-pipeline-create");
            formik.resetForm();
            setIsAdding(false);
            onCreated();
        },
        [createMutation, atPipelineLimit, scope, onCreated],
    );

    const location = useLocation();
    const addPipelineRef = React.useRef<HTMLDivElement>(null);
    React.useEffect(() => {
        if (location.hash !== "#add-pipeline") {
            return;
        }
        setIsAdding(true);
        addPipelineRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, [location.hash]);

    return (
        <div ref={addPipelineRef}>
            {atPipelineLimit ? (
                <ReferralPipelineLimitBanner />
            ) : (
                <div className="space-y-2">
                    {isAdding ? (
                        <Formik initialValues={{ name: "" }} onSubmit={save}>
                            {formValues => (
                                <form onSubmit={formValues.handleSubmit}>
                                    <div className="flex items-end space-x-1">
                                        <Input
                                            type="text"
                                            label="New pipeline"
                                            name="name"
                                            autoFocus
                                            textSize="sm"
                                            onChange={formValues.handleChange}
                                            onBlur={formValues.handleBlur}
                                            value={formValues.values.name}
                                        />
                                        <PrimaryButton
                                            disabled={
                                                formValues.isSubmitting ||
                                                formValues.values.name.trim().length === 0
                                            }
                                            type="submit">
                                            Create
                                        </PrimaryButton>
                                        <SecondaryButton
                                            type="button"
                                            onClick={stopAdding}>
                                            Cancel
                                        </SecondaryButton>
                                    </div>
                                </form>
                            )}
                        </Formik>
                    ) : (
                        <button
                            type="button"
                            onClick={startAdding}
                            className={defaultLinkStyle}>
                            <div className="flex items-center space-x-1">
                                <PlusIcon className="w-5 h-5" />
                                <span className="text-sm">{label}</span>
                            </div>
                        </button>
                    )}
                    {createMutation.isError ? (
                        <InlineBanner type="error">
                            The pipeline could not be added. Please try again.
                        </InlineBanner>
                    ) : null}
                </div>
            )}
        </div>
    );
};

const ReferralPipelineLimitBanner: React.FC<unknown> = () => {
    const productName = useProductName();
    return (
        <UpsellBanner source="referral-pipelines">
            <InlineBanner type="warning">
                Your plan includes a single referral pipeline. Additional pipelines are
                available to {productName} Premium customers. Contact <SupportEmailLink />{" "}
                to discuss an upgrade to Premium.
            </InlineBanner>
        </UpsellBanner>
    );
};

interface ReferralPipelineSettingsProps {
    pipeline: PipelineSummary;
    scope: RegionalScope;
    onChanged: () => void;
}

export const ReferralPipelineSettings: React.FC<
    ReferralPipelineSettingsProps
> = props => {
    const { pipeline, scope, onChanged } = props;
    const trpcClient = trpc.useTRPC();
    const [isRenaming, setIsRenaming] = React.useState(false);
    const renameMutation = useMutation({
        ...trpcClient.pipeline.rename.mutationOptions(),
    });
    const deleteMutation = useMutation({
        ...trpcClient.pipeline.delete.mutationOptions(),
    });
    const setAutoAddMutation = useMutation({
        ...trpcClient.pipeline.setAutoAdd.mutationOptions(),
    });

    const startRenaming = useCallback(() => {
        setIsRenaming(true);
    }, []);

    const cancelRenaming = useCallback(() => {
        setIsRenaming(false);
    }, []);

    const saveRename = useCallback(
        async (
            values: PipelineNameFormValues,
            formik: FormikHelpers<PipelineNameFormValues>,
        ) => {
            const trimmed = values.name.trim();
            if (!trimmed) {
                return;
            }
            await renameMutation.mutateAsync({
                pipelineId: pipeline.id,
                name: trimmed,
            });
            handleEvent("referral-pipeline-rename");
            formik.resetForm();
            setIsRenaming(false);
            onChanged();
        },
        [renameMutation, pipeline.id, onChanged],
    );

    const handleDelete = useCallback(async () => {
        await deleteMutation.mutateAsync({ pipelineId: pipeline.id });
        handleEvent("referral-pipeline-delete");
        onChanged();
    }, [deleteMutation, pipeline.id, onChanged]);

    const handleAutoAddChange = useCallback(
        async (autoAddNewReferralSources: boolean) => {
            await setAutoAddMutation.mutateAsync({
                pipelineId: pipeline.id,
                autoAddNewReferralSources,
            });
            handleEvent("referral-pipeline-auto-add-toggle", {
                enabled: autoAddNewReferralSources,
            });
            onChanged();
        },
        [setAutoAddMutation, pipeline.id, onChanged],
    );

    return (
        <section className="rounded-lg border border-gray-200">
            <div className="flex items-center gap-2 rounded-t-lg border-b border-gray-200 bg-gray-50 px-3 py-2.5">
                <ViewColumnsIcon className="h-5 w-5 shrink-0 text-gray-600" />
                {isRenaming ? (
                    <div className="flex-1">
                        <Formik
                            initialValues={{ name: pipeline.name }}
                            onSubmit={saveRename}>
                            {formValues => (
                                <form onSubmit={formValues.handleSubmit}>
                                    <div className="flex items-center space-x-1">
                                        <Input
                                            type="text"
                                            name="name"
                                            autoFocus
                                            textSize="sm"
                                            onChange={formValues.handleChange}
                                            onBlur={formValues.handleBlur}
                                            value={formValues.values.name}
                                        />
                                        <PrimaryButton
                                            disabled={
                                                formValues.isSubmitting ||
                                                formValues.values.name.trim().length === 0
                                            }
                                            type="submit">
                                            Save
                                        </PrimaryButton>
                                        <SecondaryButton
                                            type="button"
                                            onClick={cancelRenaming}>
                                            Cancel
                                        </SecondaryButton>
                                    </div>
                                </form>
                            )}
                        </Formik>
                    </div>
                ) : (
                    <>
                        <h3 className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-900">
                            {pipeline.name}
                        </h3>
                        <InternalLink
                            to={`/pipelines/${pipeline.id}`}
                            className="shrink-0 text-sm">
                            View pipeline
                        </InternalLink>
                        <EditButton backgroundColor="bg-white" onClick={startRenaming} />
                        <DeleteButton
                            backgroundColor="bg-white"
                            confirm={{
                                title: "Delete pipeline",
                                message: `Are you sure you want to delete the "${pipeline.name}" pipeline? Its board and stages will be removed.`,
                            }}
                            onClick={handleDelete}
                        />
                    </>
                )}
            </div>
            <div className="px-3">
                <ReferralPipelineStageEditor
                    pipeline={pipeline}
                    scope={scope}
                    onChanged={onChanged}
                />
            </div>
            <div className="border-t border-gray-200 px-3 py-2.5">
                <Toggle
                    checked={pipeline.autoAddNewReferralSources}
                    onChange={handleAutoAddChange}
                    label="Automatically add new referral sources to this pipeline"
                />
            </div>
        </section>
    );
};
